import type { ReactNode } from "react";
import { toast } from "sonner";
import { CheckCircle, TrashSimple, X } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TTone = "success" | "danger";

type TAction = { label: string; onClick: () => void };

type TAppToastProps = {
  message: ReactNode;
  tone?: TTone;
  action?: TAction;
  onClose: () => void;
};

export function AppToast({ message, tone = "success", action, onClose }: TAppToastProps) {
  const Icon = tone === "danger" ? TrashSimple : CheckCircle;

  return (
    <div
      data-testid="app-toast"
      className="flex w-[332px] max-w-[calc(100vw-2rem)] items-center gap-2 rounded-lg border border-innoveraZinc-150 bg-white p-4 shadow-200"
    >
      <span className="flex shrink-0 items-center py-0.5">
        <Icon size={16} className={cn(tone === "danger" ? "text-innoveraRed-400" : "text-innoveraGreen-600")} />
      </span>

      <div className="flex min-w-0 flex-1 items-center gap-4">
        <FigmaTypography variant="body-3" color="zinc900" className="min-w-0 flex-1 break-words">
          {message}
        </FigmaTypography>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="flex h-6 shrink-0 items-center justify-center rounded-md border border-innoveraZinc-150 px-2 text-[12px] font-medium leading-[11px] text-innoveraZinc-900 backdrop-blur-[12px] transition-colors hover:bg-innoveraZinc-50"
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex shrink-0 items-center py-0.5 text-innoveraZinc-500 transition-colors hover:text-innoveraZinc-900"
      >
        <X size={16} />
      </button>
    </div>
  );
}

type TShowAppToastParams = {
  message: ReactNode;
  tone?: TTone;
  action?: TAction;
  duration?: number;
};

export function showAppToast({ message, tone, action, duration = 5000 }: TShowAppToastParams) {
  return toast.custom(
    (id) => (
      <AppToast
        message={message}
        tone={tone}
        onClose={() => toast.dismiss(id)}
        action={
          action && {
            label: action.label,
            onClick: () => {
              action.onClick();
              toast.dismiss(id);
            }
          }
        }
      />
    ),
    { duration, unstyled: true }
  );
}
